import { useEffect, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import { getListingErrorMessage as getListingErrorMessageUtil } from 'ha/utils/availability';
import { useUnitTypeId } from 'ha/modules/MultiUnitListings/hooks';
import { track } from '@ha/analytics';
import { normalizeDate } from '@ha/date';

import {
  getIsNotFound,
  getIsUnlisted,
  isAdvertiserOwner,
  getListingDates,
  getMinimalRentalPeriod,
  getBookableWindows,
  getBookablePeriods,
  getListingId,
  getMaximumStay,
  getMaxBookableDays,
  getIsMultiUnit,
  getUnitTypeUnits,
} from '../selectors';

export function useTrackError() {
  const [searchParams] = useSearchParams();
  const { state } = useLocation();

  const { isUnitTypeId } = useUnitTypeId();

  // We need to capture only initial dates (on the page load)
  const initialLocation = useRef({
    startDate: normalizeDate(searchParams.get('startDate')),
    endDate: normalizeDate(searchParams.get('endDate')),
    trackLocation: state?.trackLocation,
  });

  const isNotFound = useSelector(getIsNotFound);
  const isUnlisted = useSelector(getIsUnlisted);
  const isOwner = useSelector(isAdvertiserOwner);
  const listingDates = useSelector(getListingDates);
  const minimumRentalPeriod = useSelector(getMinimalRentalPeriod);
  const maximumRentalPeriod = useSelector(getMaximumStay);
  const bookableWindows = useSelector(getBookableWindows);
  const bookablePeriods = useSelector(getBookablePeriods);
  const listingId = useSelector(getListingId);
  const maxBookableDays = useSelector(getMaxBookableDays);
  const isMultiUnit = useSelector(getIsMultiUnit);
  const units = useSelector(getUnitTypeUnits);

  const checkAndReportPageError = useCallback(() => {
    const listingErrorMsg = getListingErrorMessageUtil({
      isUnlisted,
      isOwner,
      desiredDates: {
        startDate: initialLocation.current?.startDate
          ? dayjs(initialLocation.current?.startDate).toDate()
          : undefined,
        endDate: initialLocation.current?.endDate
          ? dayjs(initialLocation.current?.endDate).toDate()
          : undefined,
      },
      listingDates,
      minimumRentalPeriod,
      maximumRentalPeriod,
      bookableWindows,
      bookablePeriods,
      maxBookableDays,
    });

    let trackLocation = 'unspecified';
    // in case we arrived by client-side via react-router
    if (initialLocation.current.trackLocation) {
      trackLocation = initialLocation.current.trackLocation;
      // if we got to the page with regular link and it's from our own website
    } else if (
      document.referrer &&
      new URL(document.referrer).origin === window.location.origin
    ) {
      if (document.referrer.includes('/s/')) {
        trackLocation = 'search';
      }
      if (document.referrer.includes('/room/')) {
        trackLocation = 'listing';
      }
    }

    if (isNotFound || isUnlisted || listingErrorMsg) {
      track('Listing Page Opened With Dates Error', {
        ...(isUnitTypeId
          ? {
              listingId: isMultiUnit ? undefined : units?.[0]?.id, // deprecated
              unitId: isMultiUnit ? undefined : units?.[0]?.id,
              unitTypeId: listingId,
              isMultiUnit,
            }
          : { listingId }),
        startDate: initialLocation.current.startDate,
        endDate: initialLocation.current.endDate,
        isNotFound,
        minimumRentalPeriod,
        maximumRentalPeriod,
        isUnlisted,
        dateError: isNotFound || isUnlisted ? '' : listingErrorMsg,
        trackLocation,
      });
    }
  }, [
    isOwner,
    listingId,
    isNotFound,
    isUnlisted,
    listingDates,
    minimumRentalPeriod,
    maximumRentalPeriod,
    bookableWindows,
    bookablePeriods,
    maxBookableDays,
    isUnitTypeId,
    isMultiUnit,
    units,
  ]);

  useEffect(() => {
    // due to race condition adding small timeout to prevent the event
    // from miss firing
    setTimeout(() => {
      checkAndReportPageError();
    }, 1000);
  }, [checkAndReportPageError]);
}
